import { memo, useMemo } from 'react';
import { DataTable, Pagination } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { REVIEW_PAGE_SIZES } from '../../../constants/reviewFilters.js';
import AccessCell from './AccessCell.jsx';
import EditRowButton from './EditRowButton.jsx';
import PersonCell from './PersonCell.jsx';
import StatusCell from './StatusCell.jsx';

const getRowId = (row) => row.rowNumber;

/**
 * Review table: checkbox, Row, Person, Access, Status and the Edit / Fix action, with
 * client-side pagination.
 *
 * @param {object} props
 * @param {object[]} props.rows Rows of the current page.
 * @param {{ roles: object[], groups: object[] }} props.catalog
 * @param {{ page: number, pageSize: number, totalPages: number, total: number }} props.pagination
 * @param {object} props.selection DataTable selection config.
 * @param {Map<string, string>} props.managerNames Name of each address "Reports to" can point at.
 * @param {(rowNumber: number, access: object|null) => void} props.onAccessChange
 * @param {(row: object) => void} props.onEdit
 * @param {(row: object, issue: object) => void} props.onQuickFix
 * @param {() => void} props.onPrevPage
 * @param {() => void} props.onNextPage
 * @param {(size: number) => void} props.onPageSizeChange
 */
function ReviewTable({ rows, catalog, managerNames, pagination, selection, onAccessChange, onEdit, onQuickFix, onPrevPage, onNextPage, onPageSizeChange }) {
  const { t } = useTranslation('people-access');

  const columns = useMemo(
    () => [
      { key: 'rowNumber', label: t('import.review.table.row'), cellClassName: 'tabular-nums text-content-secondary' },
      { key: 'person', label: t('import.review.table.person'), render: (_, row) => <PersonCell row={row} managerNames={managerNames} /> },
      {
        key: 'access',
        label: t('import.review.table.access'),
        render: (_, row) => <AccessCell row={row} catalog={catalog} onChange={onAccessChange} />,
      },
      {
        key: 'status',
        label: t('import.review.table.status'),
        render: (_, row) => <StatusCell row={row} onQuickFix={onQuickFix} />,
      },
      {
        key: 'actions',
        label: <span className="sr-only">{t('import.review.actions.edit')}</span>,
        render: (_, row) => <EditRowButton row={row} onEdit={onEdit} />,
      },
    ],
    [t, catalog, managerNames, onAccessChange, onEdit, onQuickFix],
  );

  const { page, pageSize, total } = pagination;
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);
  const labels = useMemo(
    () => ({
      rowsPerPage: t('import.review.table.rowsPerPage'),
      items: t('import.review.table.items', { range: `${first}–${last}`, total }),
      previousPage: t('import.review.table.previousPage'),
      nextPage: t('import.review.table.nextPage'),
    }),
    [t, first, last, total],
  );

  return (
    <div className="flex flex-1 flex-col gap-2">
      <DataTable
        columns={columns}
        data={rows}
        getRowId={getRowId}
        selection={selection}
        minTableWidth={860}
      />
      {/* Pinned to the bottom of the scroll area, just above the footer buttons, whatever the rows. */}
      <div className="sticky bottom-0 z-10 -mb-16 mt-auto bg-surface-base pb-4 pt-2">
        <Pagination
          rangeLabel={`${first}–${last}`}
          totalItems={total}
          hasPrevious={page > 1}
          hasNext={page < pagination.totalPages}
          perPage={pageSize}
          pageSizeOptions={REVIEW_PAGE_SIZES}
          onPrevPage={onPrevPage}
          onNextPage={onNextPage}
          onPageSizeChange={onPageSizeChange}
          labels={labels}
        />
      </div>
    </div>
  );
}

export default memo(ReviewTable);
